import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ProfitLog } from "../../types/profitLog.type";
import { api } from "../../api/api";
import {
  Card,
  Col,
  DatePicker,
  message,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export default function ProfitLogs() {
  const { itemId, name } = useParams();
  const [logs, setLogs] = useState<ProfitLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [totalProfit, setTotalProfit] = useState<{
    USD: number;
    UZS: number;
  }>({ USD: 0, UZS: 0 });
  const [totalExpense, setTotalExpense] = useState<{
    USD: number;
    UZS: number;
  }>({ USD: 0, UZS: 0 });
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/machine/profit-logs/${itemId}`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
        },
      });

      setLogs(response.data.logs);
      setTotalProfit(response.data.totalProfit);
      setTotalExpense(response.data.totalExpense);
      setTotal(response.data.total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение отчетов.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, pageSize, dateRange]);

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Сумма USD",
      key: "amountUSD",
      render: (record: ProfitLog) => record.amount.USD,
    },
    {
      title: "Сумма UZS",
      key: "amountUZS",
      render: (record: ProfitLog) => record.amount.UZS,
    },
  ];
  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Отчеты прибыль {name}</Title>
      <Row gutter={[16, 16]} justify="start">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card hoverable>
            <Statistic title="Прибыль USD" value={totalProfit.USD} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card hoverable>
            <Statistic title="Прибыль UZS" value={totalProfit.UZS} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card hoverable>
            <Statistic title="Расход USD" value={totalExpense.USD} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card hoverable>
            <Statistic title="Расход UZS" value={totalExpense.UZS} />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <Card hoverable>
            <Statistic
              title="Чистая прибыль USD"
              value={totalProfit.USD - totalExpense.USD}
              valueStyle={{
                color:
                  totalProfit.USD - totalExpense.USD >= 0 ? "green" : "red",
              }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <Card hoverable>
            <Statistic
              title="Чистая прибыль UZS"
              value={totalProfit.UZS - totalExpense.UZS}
              valueStyle={{
                color:
                  totalProfit.UZS - totalExpense.UZS >= 0 ? "green" : "red",
              }}
            />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16, marginTop: 16 }}>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
      </Space>
      <Table
        loading={loading}
        columns={columns}
        dataSource={logs}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
}
